/* ===================================================================
   Auth actions -- thin wrappers over supabase.auth (Hopper, 2026-06-09).

   Replaces the old 501 authStub in src/lib/api.ts. These call REAL
   Supabase Auth and return either the session or a normalized error.

   NOTE on the client<->auth linkage gap (flagged to Charlie/Steve):
   creating a Supabase Auth user does NOT by itself create a row in
   public.clients with auth_user_id = the new auth.uid(). Until that
   linkage exists, public.current_client_id() returns NULL for the new
   user and RLS returns zero rows. signUp here creates the auth identity
   only; the clients-row linkage is a backend step (see REPORT, task 4).
   =================================================================== */

import { supabase, supabaseConfigured, establishRecoverySession } from '../lib/supabase';
import { track } from '../lib/analytics';

export interface AuthResult {
  ok: boolean;
  /** True when a usable session exists immediately (no email confirm needed). */
  hasSession: boolean;
  /** Set when ok === false. Human-readable. */
  error?: string;
  /** True when signup succeeded but email confirmation is pending. */
  needsEmailConfirm?: boolean;
}

const NOT_CONFIGURED =
  'Sign-in is not available yet: the Supabase publishable key has not been ' +
  'set. (Operator: paste VITE_SUPABASE_ANON_KEY into webapp/.env.local.)';

function normalize(message: string | undefined): string {
  return message && message.trim() ? message : 'Something went wrong.';
}

/* Where to send the browser back after an OAuth round-trip or a
   password-recovery click. /auth handles both (detectSessionInUrl). */
function redirectTo(path = '/auth'): string {
  return `${window.location.origin}${path}`;
}

export async function signInWithPassword(
  email: string,
  password: string,
  captchaToken?: string,
): Promise<AuthResult> {
  if (!supabaseConfigured) return { ok: false, hasSession: false, error: NOT_CONFIGURED };
  // captchaToken is forwarded to Supabase Auth, which verifies it server-side
  // against Cloudflare Turnstile IFF CAPTCHA protection is enabled in the
  // Supabase dashboard. Undefined (the default until armed) = no change.
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
    ...(captchaToken ? { options: { captchaToken } } : {}),
  });
  if (error) return { ok: false, hasSession: false, error: normalize(error.message) };
  return { ok: true, hasSession: Boolean(data.session) };
}

export async function signUpWithPassword(
  email: string,
  password: string,
  businessName: string,
  captchaToken?: string,
): Promise<AuthResult> {
  if (!supabaseConfigured) return { ok: false, hasSession: false, error: NOT_CONFIGURED };
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      // Stash the business name on the auth user's metadata so the
      // backend linkage step (clients row) can read it later.
      data: { business_name: businessName },
      emailRedirectTo: redirectTo('/auth'),
      // Server-verified by Supabase when dashboard CAPTCHA is on. Omitted-safe.
      ...(captchaToken ? { captchaToken } : {}),
    },
  });
  if (error) return { ok: false, hasSession: false, error: normalize(error.message) };
  const hasSession = Boolean(data.session);
  track('sign_up', { needs_email_confirm: !hasSession });
  return { ok: true, hasSession, needsEmailConfirm: !hasSession };
}

export async function sendPasswordReset(
  email: string,
  captchaToken?: string,
): Promise<AuthResult> {
  if (!supabaseConfigured) return { ok: false, hasSession: false, error: NOT_CONFIGURED };
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: redirectTo('/auth'),
    // Server-verified by Supabase when dashboard CAPTCHA is on. Omitted-safe.
    ...(captchaToken ? { captchaToken } : {}),
  });
  if (error) return { ok: false, hasSession: false, error: normalize(error.message) };
  return { ok: true, hasSession: false };
}

/* Set a NEW password on the currently-authenticated user. This is the
   load-bearing step of the password-recovery flow: a recovery link logs the
   user in with a recovery session but NO durable password is set. Calling
   updateUser({ password }) persists the new password so future email/password
   logins work. Requires an active session (the recovery session provides it). */
export async function updatePassword(newPassword: string): Promise<AuthResult> {
  if (!supabaseConfigured) return { ok: false, hasSession: false, error: NOT_CONFIGURED };
  // The recovery link delivers implicit-flow tokens in the URL fragment, which
  // the PKCE client does not auto-consume. Establish the session from those
  // tokens first so updateUser has a session (fixes "Auth session missing").
  await establishRecoverySession();
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) return { ok: false, hasSession: false, error: normalize(error.message) };
  return { ok: true, hasSession: Boolean(data.user) };
}

/* Verify the 6-digit signup OTP the user just received by email, on the SAME
   screen (no context-switch to the inbox). On success Supabase establishes a
   real session, so the caller can route straight into /onboarding.

   CONFIG DEPENDENCY (Steve, at deploy): the inline 6-digit code only arrives
   if the Supabase "Confirm signup" email template includes the {{ .Token }}
   variable. If the template still sends a LINK only, this call has nothing to
   verify -- the graceful-degrade path is the magic link in the inbox, which
   SessionProvider already consumes via detectSessionInUrl. type:'signup' is the
   correct OTP type for the initial email-confirmation flow. */
export async function verifySignupOtp(
  email: string,
  token: string,
): Promise<AuthResult> {
  if (!supabaseConfigured) return { ok: false, hasSession: false, error: NOT_CONFIGURED };
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token: token.trim(),
    type: 'signup',
  });
  if (error) return { ok: false, hasSession: false, error: normalize(error.message) };
  return { ok: true, hasSession: Boolean(data.session) };
}

/* Re-send the signup confirmation email (fresh OTP code + magic link) for a
   pending, not-yet-confirmed signup. Backs the "Resend code" link on the inline
   OTP step. Same config dependency as verifySignupOtp: the code only appears if
   the "Confirm signup" template carries {{ .Token }}. */
export async function resendSignupOtp(
  email: string,
  captchaToken?: string,
): Promise<AuthResult> {
  if (!supabaseConfigured) return { ok: false, hasSession: false, error: NOT_CONFIGURED };
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: {
      emailRedirectTo: redirectTo('/auth'),
      // Server-verified by Supabase when dashboard CAPTCHA is on. Omitted-safe.
      ...(captchaToken ? { captchaToken } : {}),
    },
  });
  if (error) return { ok: false, hasSession: false, error: normalize(error.message) };
  return { ok: true, hasSession: false };
}

export async function signInWithGoogle(): Promise<AuthResult> {
  if (!supabaseConfigured) return { ok: false, hasSession: false, error: NOT_CONFIGURED };
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: redirectTo('/auth') },
  });
  // On success the browser is redirected to Google; code below this point
  // only runs on error.
  if (error) return { ok: false, hasSession: false, error: normalize(error.message) };
  return { ok: true, hasSession: false };
}
